import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { endpointService } from "#src/services/endpoint";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { ButtonV2 } from "#src/commonV2/Button";
import { IEndpointDevice } from "#src/services/endpoint/devices/types";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import { InputV2 } from "#src/commonV2/inputs/Input";

type IHideDeviceModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  deviceItem: IEndpointDevice;
  updateDeviceList: () => void;
};

export const HideDeviceModal = ({
  open,
  onClose,
  workspaceId,
  deviceItem,
  updateDeviceList,
}: IHideDeviceModalProps) => {
  const { t } = useTranslation("endpoint", { keyPrefix: "devices" });

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // --------------- COMPUTED ---------------

  const isConfirmTextValid = confirmText === deviceItem.name;

  // --------------- METHODS ---------------

  const hideDevice = async () => {
    setIsLoading(true);
    try {
      await endpointService.archiveHost(workspaceId, deviceItem.id);
      updateDeviceList();
      onClose();
    } catch (error) {
      apiErrorHandler(error, {
        toastMessage: t("notification.hideDevice.fail"),
      });
    }
    setIsLoading(false);
  };

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onClose} className="gap-8">
      <div className="flex flex-col gap-4 text-md text-gray-v2-600">
        {/* Desc */}
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold text-error-v2-600">
            {t("modals.hideDevice.title", { deviceName: deviceItem.name })}
          </span>
          <p className="text-md text-gray-v2-600">
            {t("modals.hideDevice.desc", { deviceName: deviceItem.name })}
          </p>
        </div>
        {/* Desc end */}

        {/* Confirm */}
        <div className="flex flex-col gap-1.5">
          <Trans
            t={t}
            i18nKey={"modals.hideDevice.confirm_desc"}
            values={{ deviceName: deviceItem.name }}
            components={{ b: <b className="contents text-gray-v2-700" /> }}
          />
          <InputV2
            value={confirmText}
            onChange={(e) => {
              setConfirmText(e.target.value);
            }}
            className="w-full"
          />
        </div>
        {/* Confirm end */}
      </div>

      {/* Footer */}
      <div className="flex gap-2 justify-end">
        <ButtonV2 variant="secondary" size="lg" color="gray" onClick={onClose}>
          <p>{t("button.cancel")}</p>
        </ButtonV2>

        <ButtonV2
          variant="primary"
          size="lg"
          color="error"
          pending={isLoading}
          onClick={hideDevice}
          disabled={!isConfirmTextValid}
        >
          <p>{t("button.confirm")}</p>
        </ButtonV2>
      </div>
      {/* Footer end */}
    </ModalV2>
  );
};
